// Copyright (c) Docugami, Inc. All rights reserved.

import { NetworkRequestInfo } from "networking/NetworkRequestInfo";
import { InteractionStates, NotificationModel, NotificationsApi } from "networking/NotificationsApi";
import { IObservableCollection, ObservableArray } from "utils/ObservableArray";

interface NotificationsRequests {
    fetchNotifications: NetworkRequestInfo;
}

class NotificationsManager {
    private _notifications: ObservableArray<NotificationModel> =
        new ObservableArray<NotificationModel>(
            [],
            (item1, item2) => item1.id === item2.id);

    public readonly requests: NotificationsRequests = {
        fetchNotifications: {}
    }

    public configureHub(connection: signalR.HubConnection) {
        const notificationsManager = this;
        connection.on("addNotification", (notification: NotificationModel) => {
            notificationsManager._notifications.addOrUpdate(notification);
        });

        connection.on("removeNotification", (notification: NotificationModel) => {
            notificationsManager._notifications.remove(notification);
        });

        connection.on("modifyNotification", (notification: NotificationModel) => {
            notificationsManager._notifications.addOrUpdate(notification);
        });
    }

    public get notifications(): NotificationModel[] {
        return [...this._notifications.storage];
    }

    public getNotification(id: string): NotificationModel | undefined {
        return this._notifications.find("id", id);
    }

    public get observableNotifications(): IObservableCollection<NotificationModel> {
        return this._notifications;
    }

    public async fetchNotifications(orgId: string): Promise<void> {
        if (!this.requests.fetchNotifications.promise) {
            this.requests.fetchNotifications.lastRequestTime = new Date();
            this.requests.fetchNotifications.promise = this.fetchNotificationsInternal(orgId);
        }

        return await this.requests.fetchNotifications.promise;
    }

    public async markAsRead(orgId: string, ids: string[]) {
        this.cleanseIds(ids);

        if (ids.length === 0) {
            return Promise.resolve();
        }

        function read(item: NotificationModel) {
            item.interactions |= InteractionStates.Read;
        }

        function revert(item: NotificationModel) {
            item.interactions &= ~InteractionStates.Read;
        }

        const notificationsApi = new NotificationsApi();
        try {
            this.apply(ids, read);
            await notificationsApi.markAsRead(orgId, ids);
        }
        catch(error) {
            this.apply(ids, revert);
        }
    }

    private async fetchNotificationsInternal(orgId: string): Promise<void> {
        try {
            const notificationsApi = new NotificationsApi();
            const response = await notificationsApi.getNotifications(orgId);

            this._notifications.reset(...response.data);
            this.requests.fetchNotifications.succeeded = true;
        }
        finally {
            this.requests.fetchNotifications.promise = undefined;
        }
    }

    private cleanseIds(ids: string[]) {
        for (let i = ids.length - 1; i >= 0; --i) {
            const id = ids[i];
            const index = this._notifications.indexOf("id", id);
            if (index === -1) {
                ids = ids.splice(i, 1);
            }
        }
    }

    private apply(ids: string[], update: (item: NotificationModel) => void) {
        for (const id of ids) {
            const index = this._notifications.indexOf("id", id);
            if (index !== -1) {
                const item = this._notifications.get(index);
                update(item);
                this._notifications.set(index, item);
            }
        }
    }
}

export const NotificationsManagerSingleton = new NotificationsManager();
